import axios, { AxiosRequestConfig, AxiosResponse } from "axios";
import toast from "react-hot-toast";

export class ApiService {
  private api;

  constructor(baseURL: string) {
    this.api = axios.create({
      baseURL,
      headers: {
        Authorization: "Bearer " + localStorage.getItem("token"),
      },
    });
  }

  async get<T>(endpoint: string, params: Record<string, any> = {}): Promise<T> {
    return this.makeRequest<T>("GET", endpoint, null, params);
  }

  async post<T>(endpoint: string, data: Record<string, any>): Promise<T> {
    return this.makeRequest<T>("POST", endpoint, data);
  }

  async put<T>(endpoint: string, data: Record<string, any>): Promise<T> {
    return this.makeRequest<T>("PUT", endpoint, data);
  }

  async delete<T>(endpoint: string): Promise<T> {
    return this.makeRequest<T>(
      "DELETE",
      endpoint,
      null,
      {},
      { omitContentType: true }
    );
  }

  async postFormData<T>(
    endpoint: string,
    formData: FormData,
    headers?: { method: "POST" | "PUT" }
  ): Promise<T> {
    try {
      if (headers?.method?.toLocaleLowerCase() === "post") {
        const response: AxiosResponse<T> = await this.api.post(
          endpoint,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
              Authorization: "Bearer " + localStorage.getItem("token"),
            },
          }
        );
        return response.data;
      } else {
        const response: AxiosResponse<T> = await this.api.put(
          endpoint,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
              Authorization: "Bearer " + localStorage.getItem("token"),
            },
          }
        );
        return response.data;
      }
    } catch (error: any) {
      if (error?.code === "ERR_NETWORK") {
        toast.error("Network Error !");
      }
      if (error?.response?.status === 401) {
        const toastId = toast.error("Session Expired!");
        setTimeout(() => {
          toast.remove(toastId);
          localStorage.removeItem("user");
          localStorage.removeItem("token");
          window.location.href = "/login";
        }, 1000);
      }
      return error;
    }
  }

  private async makeRequest<T>(
    method: string,
    endpoint: string,
    data: Record<string, any> | null = null,
    params: Record<string, any> = {},
    options: { omitContentType?: boolean } = {}
  ): Promise<T> {
    try {
      const headers: Record<string, string> = {
        Authorization: "Bearer " + localStorage.getItem("token"),
      };

      if (!options.omitContentType) {
        headers["Content-Type"] = "application/json";
      }

      const response: AxiosResponse<T> = await this.api.request<T>({
        method,
        url: endpoint,
        data,
        params,
        headers,
      });

      return response.data;
    } catch (error: any) {
      if (error?.code === "ERR_NETWORK") {
        toast.error("Network Error !");
      }
      if (error?.response?.status > 400 && error?.response?.status !== 401) {
        const toastId = toast.error(error?.response?.data?.message);
        setTimeout(() => {
          toast.remove(toastId);
        }, 2000);
      }
      if (error?.response?.status === 401) {
        const toastId = toast.error("Session Expired! Please log in again");
        setTimeout(() => {
          toast.remove(toastId);
          localStorage.removeItem("user");
          localStorage.removeItem("token");
          window.location.href = "/login";
        }, 1200);
      }
      return error;
    }
  }
}

// // Example usage:
// (async () => {
//   try {
//     const api = new ApiService('https://jsonplaceholder.typicode.com');

//     // Example of GET request
//     const getResult = await api.get<{ title: string }>('todos/1');
//     console.log('GET Response:', getResult);

//     // Example of POST request
//     // const postData = { title: 'foo', body: 'bar', userId: 1 };
//     // const postResult = await api.post('/posts', postData);
//     // console.log('POST Response:', postResult);

//     // Example of PUT request
//     // const putData = { id: 1, title: 'foo', body: 'bar', userId: 1 };
//     // const putResult = await api.put('/posts/1', putData);
//     // console.log('PUT Response:', putResult);

//     // Example of DELETE request
//     // const deleteResult = await api.delete('/posts/1');
//     // console.log('DELETE Response:', deleteResult);

//     // Example of POST request with form data
//     // const formData = new FormData();
//     // formData.append('file', file); // Assuming 'file' is a file input element
//     // const formPostResult = await api.postFormData('/upload', formData);
//     // console.log('Form Data POST Response:', formPostResult);
//   } catch (error) {
//     console.error('Error making API call:', error.message);
//   }
// })();
